import Button from "@app/components/button";
import CustomAsyncSelect from "@app/components/custom-async-select";
import { PromiseOptionsCallback } from "@app/components/custom-async-select/custom-async-select.component";
import CustomReactSelect from "@app/components/custom-react-select";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import Textarea from "@app/components/textarea";
import { addToast } from "@app/components/toast/toast.service";
import {
  RoutePath,
  SystemMessage,
  BUCKET_URL_KEY,
  MAX_SIZE_UPLOAD,
} from "@app/constants";
import AffiliateCampaignService from "@app/services/http/affiliate-campaign.service";
import BookSegmentService from "@app/services/http/book-segment.service";
import UploadMediaService from "@app/services/http/upload-media.service";
import {
  UpdateAffiliateCampaign,
  CampaignType,
  CampaignTypeOptions,
} from "@app/types";
import { updateAffiliateCampaignValidationSchema } from "@app/validations/affiliate-campaign.validation";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import StorageService from "@core/services/storage/storage.service";
import { Images } from "@assets/images";
import { Form, Formik, FormikProps, useFormikContext } from "formik";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useCallback, useEffect, useRef, useState, ChangeEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";

interface UpdateAffiliateCampaignForm {
  type: number;
  book: { label: string; value: number } | null;
  content: string;
  kas?: number;
  vipTime?: number;
  thumbnail?: string;
  startDate: string;
  endDate: string;
}

// Component upload ảnh với Formik context
const CampaignImageUpload = () => {
  const formik = useFormikContext<UpdateAffiliateCampaignForm>();
  const [campaignImageFile, setCampaignImageFile] = useState<File | null>(null);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const s3Url = StorageService.getObject(BUCKET_URL_KEY);

  const handleImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) {
      setCampaignImageFile(null);
      formik.setFieldValue("thumbnail", "");

      return;
    }

    const file = event.target.files[0];

    if (file.size > MAX_SIZE_UPLOAD) {
      addToast({ text: SystemMessage.MAX_SIZE_UPLOAD, status: "inValid" });
      setCampaignImageFile(null);
      formik.setFieldValue("thumbnail", "");

      return;
    }

    setCampaignImageFile(file);

    // Upload the file immediately
    UploadMediaService.upload({ medias: [file] })
      .then((response) => {
        if (response.data && response.data.items[0]) {
          const uploadedImageUrl = response.data.items[0].url;

          formik.setFieldValue("thumbnail", uploadedImageUrl);
          addToast({ text: "Upload ảnh thành công!" });
        }
      })
      .catch(() => {
        addToast({ text: "Lỗi khi upload ảnh", status: "inValid" });
        setCampaignImageFile(null);
        formik.setFieldValue("thumbnail", "");
      });
  };

  return (
    <div className="mt-7">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Ảnh Campaign <span className="text-red-500">*</span>
      </label>

      {/* Image Preview */}
      {(campaignImageFile || formik.values.thumbnail) && (
        <div className="mb-4">
          <img
            src={
              campaignImageFile
                ? URL.createObjectURL(campaignImageFile)
                : s3Url + (formik.values.thumbnail || "")
            }
            alt="Preview"
            className="w-32 h-32 object-cover rounded-lg border border-gray-200"
          />
        </div>
      )}

      <label
        className={`rounded-lg px-4 py-2 h-[52px] cursor-pointer flex items-center justify-between ${
          formik.errors.thumbnail && formik.touched.thumbnail
            ? "bg-red-50 border-2 border-red-500"
            : "bg-[rgba(239,241,249,0.6)]"
        }`}
      >
        <input
          ref={inputFileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageUpload}
        />
        <div className="text-[#ABAFB1] text-base truncate mr-3">
          {campaignImageFile?.name ||
            formik.values.thumbnail ||
            "⚠️ Vui lòng upload ảnh campaign (BẮT BUỘC)"}
        </div>
        <img src={Images.UploadIcon.default} alt="upload" className="w-6 h-6" />
      </label>

      {/* Error message */}
      {formik.errors.thumbnail && (
        <div className="text-red-500 text-sm mt-2 font-semibold">
          ⚠️ {formik.errors.thumbnail} - Bạn PHẢI UPLOAD ẢNH trước khi lưu!
        </div>
      )}

      {/* Success message */}
      {formik.values.thumbnail && (
        <div className="text-green-600 text-sm mt-2 font-semibold">
          ✓ Đã upload ảnh thành công!
        </div>
      )}
    </div>
  );
};

const UpdateAffiliateCampaignPage = () => {
  const { affiliateCampaignId } = useParams();
  const navigate = useNavigate();
  const { abortController } = useCancellationRequest();

  const [campaignType, setCampaignType] = useState<number>(
    CampaignType.GIFT_BOOK
  );

  const formRef = useRef<FormikProps<UpdateAffiliateCampaignForm>>(null);

  useEffect(() => {
    if (isNaN(Number(affiliateCampaignId))) {
      return navigate(`/${RoutePath.AFFILIATE_CAMPAIGN_MANAGEMENT}`);
    }

    AffiliateCampaignService.get(Number(affiliateCampaignId)).then((res) => {
      const {
        type,
        bookId,
        bookName,
        content,
        kas,
        vipTime,
        thumbnail,
        startDate,
        endDate,
      } = res.data as any;

      /* eslint-disable padding-line-between-statements */
      const formatDateForInput = (
        input: number | string | null | undefined
      ) => {
        if (input === null || input === undefined) return "";
        dayjs.extend(customParseFormat);

        const toYMD = (d: dayjs.Dayjs) => d.format("YYYY-MM-DD");

        if (typeof input === "number") {
          const ms = input < 1e12 ? input * 1000 : input;
          const d = dayjs(ms);
          return d.isValid() ? toYMD(d) : "";
        }

        const trimmed = String(input).trim();
        if (!trimmed) return "";

        if (/^\d+$/.test(trimmed)) {
          const n = Number(trimmed);
          const ms = n < 1e12 ? n * 1000 : n;
          const d = dayjs(ms);
          return d.isValid() ? toYMD(d) : "";
        }

        let d = dayjs(trimmed, "DD/MM/YYYY", true);
        if (d.isValid()) return toYMD(d);

        d = dayjs(trimmed);
        return d.isValid() ? toYMD(d) : "";
      };
      /* eslint-enable padding-line-between-statements */

      // Set campaign type state
      setCampaignType(type || CampaignType.GIFT_BOOK);

      formRef.current?.setValues({
        type: type || CampaignType.GIFT_BOOK,
        book: bookName ? { label: bookName, value: bookId } : null,
        content: content ?? "",
        kas: kas || undefined,
        vipTime: vipTime || undefined,
        thumbnail: thumbnail || "",
        startDate: formatDateForInput(startDate),
        endDate: formatDateForInput(endDate),
      });
    });
  }, [affiliateCampaignId]);

  const onSubmit = (values: UpdateAffiliateCampaignForm) => {
    if (!formRef.current) return;

    const affiliateCampaign: UpdateAffiliateCampaign = {
      type: values.type,
      content: values.content,
      startDate: values.startDate || null,
      endDate: values.endDate || null,
    };

    // Add type-specific fields
    if (values.type === CampaignType.GIFT_BOOK && values.book) {
      affiliateCampaign.bookId = values.book.value;
    } else if (values.type === CampaignType.GIFT_KAS) {
      affiliateCampaign.kas = values.kas;
      affiliateCampaign.thumbnail = values.thumbnail;
    } else if (values.type === CampaignType.GIFT_VIP) {
      affiliateCampaign.vipTime = values.vipTime;
      affiliateCampaign.thumbnail = values.thumbnail;
    }

    AffiliateCampaignService.update(
      Number(affiliateCampaignId),
      affiliateCampaign
    )
      .then(() => {
        addToast({
          text: SystemMessage.UPDATE_SUCCESSFULLY || "Cập nhật thành công",
        });
        // Navigate to affiliate campaign list after successful update
        navigate(`/${RoutePath.AFFILIATE_CAMPAIGN_MANAGEMENT}`);
      })
      .catch(() => {
        addToast({ text: "Có lỗi xảy ra khi cập nhật campaign", status: "inValid" });
      });
  };

  const getBooksOptions = useCallback(
    (inputValue: string, callback: PromiseOptionsCallback) => {
      // Use admin dashboard book-dropdown API to fetch retail books (bookOption=3)
      BookSegmentService.getBookDropdown(
        inputValue.trim(),
        abortController.signal
      ).then((res) => {
        callback(res.data.items);
      });
    },
    [abortController.signal]
  );

  const gotoListAffiliateCampaign = () => {
    navigate(`/${RoutePath.AFFILIATE_CAMPAIGN_MANAGEMENT}`);
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center ">
        <div className="text-[#343C6A]">Danh sách campaign / Sửa</div>
      </div>

      <Formik
        innerRef={formRef}
        initialValues={{
          type: CampaignType.GIFT_BOOK,
          book: null,
          content: "",
          kas: undefined,
          vipTime: undefined,
          thumbnail: "",
          startDate: "",
          endDate: "",
        }}
        onSubmit={onSubmit}
        validationSchema={updateAffiliateCampaignValidationSchema}
        validateOnChange
        validateOnBlur
      >
        <Form>
          <div className="mt-6 rounded-xl bg-white py-6 px-5">
            <div className="w-[100%] max-w-[700px]">
              {/* Campaign Type Dropdown - Disabled in update mode */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Loại Campaign
                </label>
                <CustomReactSelect
                  options={CampaignTypeOptions}
                  value={CampaignTypeOptions.find(
                    (opt) => opt.value === campaignType
                  )}
                  onChange={() => {}} // Disabled - cannot change type in update mode
                  placeholder="Chọn loại campaign"
                  isClearable={false}
                  isDisabled={true}
                />
              </div>

              {/* Book Selection - Only for GIFT_BOOK */}
              {campaignType === CampaignType.GIFT_BOOK && (
                <FormControl name="book">
                  <CustomAsyncSelect
                    className="mt-7 rounded-lg"
                    promiseOptions={getBooksOptions}
                    placeholder="Chọn sách"
                    styles={{
                      stylesControl: {
                        minHeight: "52px",
                      },
                    }}
                  />
                </FormControl>
              )}

              {/* Kas Field - Only for GIFT_KAS */}
              {campaignType === CampaignType.GIFT_KAS && (
                <FormControl name="kas">
                  <Input className="mt-7" type="number" placeholder="Số kas" />
                </FormControl>
              )}

              {/* VIP Time Field - Only for GIFT_VIP */}
              {campaignType === CampaignType.GIFT_VIP && (
                <FormControl name="vipTime">
                  <Input
                    className="mt-7"
                    type="number"
                    placeholder="Thời gian VIP (giây)"
                  />
                </FormControl>
              )}

              {/* Campaign Image - For GIFT_KAS and GIFT_VIP */}
              {(campaignType === CampaignType.GIFT_KAS ||
                campaignType === CampaignType.GIFT_VIP) && (
                <CampaignImageUpload />
              )}

              {/* Content Field - Always visible */}
              <FormControl name="content">
                <Textarea
                  className="mt-7"
                  width="100%"
                  placeholder="Nội dung campaign"
                  rows={4}
                />
              </FormControl>

              {/* Date Fields - Always visible */}
              <FormControl name="startDate">
                <Input
                  className="mt-7"
                  type="date"
                  placeholder="Ngày bắt đầu"
                />
              </FormControl>
              <FormControl name="endDate">
                <Input
                  className="mt-7"
                  type="date"
                  placeholder="Ngày kết thúc"
                />
              </FormControl>
            </div>
          </div>
          <div className="mt-6 flex justify-left gap-4">
            <Button
              type="submit"
              size="xs"
              label={"Lưu"}
              width={180}
              className="!h-[51px] !leading-[51px]"
              labelClassName="text-xl"
            />
            <Button
              size="xs"
              label="Hủy"
              theme="secondary"
              width={180}
              className="!h-[51px] !leading-[51px]"
              labelClassName="text-xl"
              onClick={gotoListAffiliateCampaign}
            />
          </div>
        </Form>
      </Formik>
    </div>
  );
};

export default UpdateAffiliateCampaignPage;
